import React from "react";
import { useQuery, gql } from "@apollo/client";

// Everything changed about one professional: every edit to their record
// (pay, dates, active / dummy, contact, slots ... and who made it - an
// admin, the professional in the app, or the system), their own leave
// policies, balance adjustments, bonuses / deductions, fines switched
// imposed <-> warning, and questioned deductions decided. From 23 Oct 2026.

const GET_EMPLOYEE_CHANGE_HISTORY = gql`
  query GetEmployeeChangeHistory($employeeId: ID!) {
    getEmployeeChangeHistory(employeeId: $employeeId) {
      status
      message
      entries {
        id
        changedAt
        changedByName
        area
        summary
      }
    }
  }
`;

const AREA_BADGE = {
  Created: "bg-gray-200 text-gray-800",
  Record: "bg-sky-100 text-sky-800",
  Pay: "bg-purple-100 text-purple-800",
  Fine: "bg-red-100 text-red-800",
  Adjustment: "bg-orange-100 text-orange-800",
  "Leave policy": "bg-blue-100 text-blue-800",
  "Leave balance": "bg-green-100 text-green-800",
  Question: "bg-yellow-100 text-yellow-800",
};

/** The history table on its own - used by the modal (Edit employee) and
 * the History tab of the employee dashboard. */
export const EmployeeChangeHistory = ({ employeeId }) => {
  const { data, loading } = useQuery(GET_EMPLOYEE_CHANGE_HISTORY, {
    variables: { employeeId },
    fetchPolicy: "network-only",
    skip: !employeeId,
  });
  const res = data?.getEmployeeChangeHistory;
  const entries = res?.entries || [];

  if (loading) return <p className="text-sm text-gray-500">Loading...</p>;
  if (res?.status === "ERROR") return <p className="text-sm text-red-600">{res.message}</p>;
  if (entries.length === 0) {
    return (
      <p className="text-sm text-gray-500">
        No changes recorded yet. Every edit to the record (by admins, the app or the system) is
        recorded from 23 Oct 2026; leave balance adjustments and questioned deductions from
        when those features started.
      </p>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-left text-sm text-gray-600">
        <thead className="bg-gray-50 text-gray-700 border-b">
          <tr>
            <th className="py-2 px-3">When</th>
            <th className="py-2 px-3">Who</th>
            <th className="py-2 px-3">What</th>
            <th className="py-2 px-3">Change</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {entries.map((e) => (
            <tr key={`${e.area}-${e.id}`}>
              <td className="py-2 px-3 whitespace-nowrap">
                {e.changedAt
                  ? new Date(e.changedAt).toLocaleString("en-IN", {
                      timeZone: "Asia/Kolkata",
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                      hour: "numeric",
                      minute: "2-digit",
                    })
                  : "—"}
              </td>
              <td className="py-2 px-3">{e.changedByName || "—"}</td>
              <td className="py-2 px-3">
                <span
                  className={`px-2 py-0.5 rounded text-xs font-semibold whitespace-nowrap ${
                    AREA_BADGE[e.area] || "bg-gray-100 text-gray-700"
                  }`}
                >
                  {e.area}
                </span>
              </td>
              <td className="py-2 px-3 text-gray-800">{e.summary}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const EmployeeChangeHistoryModal = ({ employeeId, name, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl p-6 max-h-[85vh] overflow-y-auto">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-gray-800">Employee history · {name}</h2>
        <button onClick={onClose} className="text-gray-500 hover:text-gray-800 text-xl leading-none">
          ×
        </button>
      </div>
      <EmployeeChangeHistory employeeId={employeeId} />
    </div>
  </div>
);

export default EmployeeChangeHistoryModal;
